import { deleteApp, initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import {
  getAuth,
  connectAuthEmulator,
  inMemoryPersistence,
  initializeRecaptchaConfig,
  setPersistence,
  signOut,
} from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { captureException } from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';

import { isRecaptchaKeyUndefinedError } from './utils/sentryHelpers';

// App Check debug token must be set BEFORE initializeAppCheck is called.
// In development, use a registered debug token from VITE_FIREBASE_APPCHECK_DEBUG_TOKEN.
// If not set, fallback to `true` which auto-generates a token (must be registered in Firebase Console).
if (import.meta.env.DEV) {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN ?? true;
}

const recaptchaPublicKey = import.meta.env.VITE_APP_RECAPTCHA_PUBLIC_KEY;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_APP_FIREBASE_CONFIG_API_KEY,
  authDomain: import.meta.env.VITE_APP_FIREBASE_CONFIG_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_APP_FIREBASE_CONFIG_PROJECT_ID,
  storageBucket: import.meta.env.VITE_APP_FIREBASE_CONFIG_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_APP_FIREBASE_CONFIG_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_FIREBASE_CONFIG_APP_ID,
  measurementId: import.meta.env.VITE_APP_FIREBASE_CONFIG_MEASUREMENT_ID,
};

// Initialize Firebase & Firebase services
const firebaseApp = initializeApp(firebaseConfig);

// To be used again once all of the areas with the undefined properties are fixed
// const db = getFirestore(firebaseApp);
// temporarily using this to lessen the noise in the sentry
const db = initializeFirestore(firebaseApp, { ignoreUndefinedProperties: true });
const auth = getAuth(firebaseApp);
const storage = getStorage(firebaseApp);
const functions = getFunctions(firebaseApp, 'australia-southeast1');

if (import.meta.env.DEV) {
  window.__fbApp = firebaseApp;
  window.__fbAuth = auth;
  window.__firestoreDB = db;

  if (import.meta.env.VITE_FIREBASE_USE_EMULATOR_FIRESTORE === 'true') {
    connectFirestoreEmulator(db, 'localhost', 8080);
  }

  if (import.meta.env.VITE_FIREBASE_USE_EMULATOR_AUTH === 'true') {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  }

  if (import.meta.env.VITE_FIREBASE_USE_EMULATOR_STORAGE === 'true') {
    connectStorageEmulator(storage, 'localhost', 9199);
  }

  if (import.meta.env.VITE_FIREBASE_USE_EMULATOR_FUNCTIONS === 'true') {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  }
}

/**
 * Load reCAPTCHA Enterprise script
 * We needed this otherwise it won't work. Simply importing ReCaptchaEnterpriseProvider is not enough and doesn't load it correctly.
 * Could be a bug with the Firebase SDK.
 * @returns {Promise<void>}
 */
const loadRecaptchaEnterprise = () =>
  new Promise((resolve, reject) => {
    if (window.grecaptcha?.enterprise) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaPublicKey}`;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

/**
 * Initialize auth reCAPTCHA configuration
 * @returns {Promise<void>}
 */
const initializeAuthRecaptcha = async () => {
  try {
    await loadRecaptchaEnterprise();
    await initializeRecaptchaConfig(auth);
  } catch (err) {
    if (isRecaptchaKeyUndefinedError(err)) {
      return;
    }

    captureException(err, {
      level: 'error',
      tags: { firebase: 'recaptcha' },
      extra: { message: 'Failed to initialize Firebase Auth reCAPTCHA Enterprise' },
    });
  }
};

if (!(import.meta.env.DEV && import.meta.env.VITE_FIREBASE_USE_EMULATOR_AUTH === 'true')) {
  initializeAuthRecaptcha();
}

/**
 * Creates a temporary, isolated Auth instance for credential probing/recovery flows.
 * The returned auth uses in-memory persistence so it won't persist a browser session.
 *
 * @param {{ prefix?: string }} [options]
 * @returns {Promise<{ probeAuth: import('firebase/auth').Auth, dispose: () => Promise<void> }>}
 */
const createRecoveryProbeAuth = async ({ prefix = 'auth-recovery-probe' } = {}) => {
  const appName = `${prefix}-${uuidv4()}`;
  const probeApp = initializeApp(auth.app.options, appName);
  const probeAuth = getAuth(probeApp);
  const cleanup = async () => {
    await deleteApp(probeApp).catch(() => {});
  };

  try {
    if (import.meta.env.VITE_FIREBASE_USE_EMULATOR_AUTH === 'true') {
      connectAuthEmulator(probeAuth, 'http://localhost:9099', { disableWarnings: true });
    } else {
      initializeAppCheck(probeApp, {
        provider: new ReCaptchaEnterpriseProvider(recaptchaPublicKey),
        isTokenAutoRefreshEnabled: true,
      });

      await loadRecaptchaEnterprise();

      try {
        await initializeRecaptchaConfig(probeAuth);
      } catch (error) {
        if (!isRecaptchaKeyUndefinedError(error)) {
          throw error;
        }
      }
    }

    await setPersistence(probeAuth, inMemoryPersistence);
  } catch (error) {
    await cleanup();
    throw error;
  }

  return {
    probeAuth,
    dispose: async () => {
      if (probeAuth.currentUser) await signOut(probeAuth).catch(() => {});

      await cleanup();
    },
  };
};

// Initialize App Check (for Firestore/Functions/Storage protection)
const appCheck = initializeAppCheck(firebaseApp, {
  provider: new ReCaptchaEnterpriseProvider(recaptchaPublicKey),
  isTokenAutoRefreshEnabled: true,
});

export { firebaseApp, appCheck, db, auth, functions, storage, createRecoveryProbeAuth };
